import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function initWorkIntroAnimation() {
    const intros = document.querySelectorAll('.work-intro');

    if (!intros.length) {
        return null;
    }

    intros.forEach((intro) => {
        const breadcrumb = intro.querySelector('.work-intro__breadcrumb');
        const title = intro.querySelector('.work-intro__title');
        const description = intro.querySelector('.work-intro__description');
        const footer = intro.querySelector('.work-intro__footer');
        const imageCol = intro.querySelector('.work-intro__image-col');

        if (breadcrumb) {
            gsap.set(breadcrumb, { opacity: 0, y: 24 });
        }
        if (title) {
            gsap.set(title, { opacity: 0, y: 130, skewY: 4 });
        }
        if (description) {
            gsap.set(description, { opacity: 0, y: 70 });
        }
        if (footer) {
            gsap.set(footer, { opacity: 0, x: -50 });
        }
        if (imageCol) {
            gsap.set(imageCol, {
                opacity: 0,
                scale: 0.84,
                clipPath: 'inset(8% 0% 8% 0%)',
                transformOrigin: 'center center'
            });
        }

        const tl = gsap.timeline({ delay: 0.15 });

        if (breadcrumb) {
            tl.fromTo(
                breadcrumb,
                { opacity: 0, y: 24 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
            );
        }

        if (title) {
            tl.fromTo(
                title,
                { opacity: 0, y: 130, skewY: 4 },
                { opacity: 1, y: 0, skewY: 0, duration: 1.1, ease: 'expo.out' },
                '-=0.2'
            );
        }

        if (description) {
            tl.fromTo(
                description,
                { opacity: 0, y: 70 },
                { opacity: 1, y: 0, duration: 1, ease: 'power3.out' },
                '-=0.65'
            );
        }

        if (footer) {
            tl.fromTo(
                footer,
                { opacity: 0, x: -50 },
                { opacity: 1, x: 0, duration: 1, ease: 'power3.out' },
                '-=0.75'
            );
        }

        if (imageCol) {
            tl.fromTo(
                imageCol,
                { opacity: 0, scale: 0.84, clipPath: 'inset(8% 0% 8% 0%)' },
                {
                    opacity: 1,
                    scale: 1,
                    clipPath: 'inset(0% 0% 0% 0%)',
                    duration: 1.7,
                    ease: 'power3.out'
                },
                '-=1.3'
            );
        }
    });
}

function initWorkBlockAnimations() {
    if (!document.querySelector('.work-block')) {
        return null;
    }

    document.querySelectorAll('.work-block').forEach((section) => {
        const title = section.querySelector('.work-block__title');
        const text = section.querySelector('.work-block__text');
        const img = section.classList.contains('work-block--risultati') ? null : section.querySelector('.work-block__img');

        const tl = gsap.timeline({
            scrollTrigger: {trigger: section, start: 'top 62%', toggleActions: 'play none none none'}
        });

        if (title) {
            tl.fromTo(title,
                {opacity: 0, y: 100, skewY: 3},
                {opacity: 1, y: 0, skewY: 0, duration: 1.1, ease: 'expo.out'}
            );
        }

        if (text) {
            tl.fromTo(text,
                {opacity: 0, y: 65},
                {opacity: 1, y: 0, duration: 1, ease: 'power3.out'},
                '-=0.65'
            );
        }

        if (img) {
            tl.fromTo(img,
                {opacity: 0, scale: 0.84, clipPath: 'inset(8% 0% 8% 0%)', transformOrigin: 'center center'},
                {opacity: 1, scale: 1, clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'power3.out'},
                '-=0.85'
            );
        }
    });

    // Risultati: le 2 immagini entrano dal basso con scale
    document.querySelectorAll('.work-block--risultati .work-block__img--risultati').forEach((img, i) => {
        gsap.fromTo(img,
            {opacity: 0, y: 100, scale: 0.88},
            {
                opacity: 1, y: 0, scale: 1, duration: 1.4, ease: 'expo.out',
                scrollTrigger: {trigger: img, start: 'top 65%'},
                delay: i * 0.2
            }
        );
    });
}

// Work page — animazioni dynamic content (nuovo layout) + gallery + video
// .wdc-section: alternate slide sx/dx + clip-path sulle immagini.
// ─────────────────────────────────────────────────────────────────────────────
function initWorkDynamicContentAnimations() {
    // Video: zoom-in dal basso
    const videoSection = document.querySelector('.work-video');
    if (videoSection) {
        gsap.fromTo(videoSection,
            {opacity: 0, y: 80, scale: 0.96},
            {
                opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'power3.out',
                scrollTrigger: {trigger: videoSection, start: 'top 72%'}
            }
        );
    }

    // .wdc-section: colonne di testo alternate sx/dx, immagini con clip-path wipe
    document.querySelectorAll('.wdc-section').forEach((section, i) => {
        const inner = section.querySelector(':scope > [class*="col-"]');
        const target = inner || section;
        const fromX = i % 2 === 0 ? -70 : 70;

        gsap.fromTo(target,
            {opacity: 0, y: 70, x: fromX},
            {
                opacity: 1, y: 0, x: 0, duration: 1.1, ease: 'expo.out',
                scrollTrigger: {trigger: section, start: 'top 70%', toggleActions: 'play none none none'}
            }
        );

        // Immagini: clip-path wipe verticale + scale
        const imgs = section.querySelectorAll('img.img-fluid, .ratio-content');
        imgs.forEach((img, j) => {
            gsap.fromTo(img,
                {scale: 0.86, opacity: 0, clipPath: 'inset(12% 0% 12% 0%)', transformOrigin: 'center center'},
                {
                    scale: 1, opacity: 1, clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5, ease: 'power3.out',
                    delay: j * 0.15,
                    scrollTrigger: {trigger: section, start: 'top 70%', toggleActions: 'play none none none'}
                }
            );
        });
    });

    // Gallery items: già visibili → stagger immediato, sotto la piega → scroll-triggered
    const galleryItems = document.querySelectorAll('.work-gallery .work-gallery__item');
    if (galleryItems.length) {
        let inViewCount = 0;
        galleryItems.forEach((item, index) => {
            const inView = item.getBoundingClientRect().top < window.innerHeight;
            if (inView) {
                gsap.fromTo(item,
                    {opacity: 0, y: 90, scale: 0.88},
                    {opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'expo.out', delay: 0.9 + (inViewCount * 0.14)}
                );
                inViewCount++;
            } else {
                gsap.fromTo(item,
                    {opacity: 0, y: 90, scale: 0.88},
                    {
                        opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'expo.out',
                        delay: (index % 2) * 0.14,
                        scrollTrigger: {trigger: item, start: 'top 88%', toggleActions: 'play none none none'}
                    }
                );
            }
        });
    }
}

function initMasonryGallery() {
    const grids = document.querySelectorAll('.work-gallery-masonry');
    if (!grids.length) {
        return;
    }

    grids.forEach(grid => {
        const items = Array.from(grid.querySelectorAll('.work-gallery-masonry__item'));
        let animated = false;

        function getColCount() {
            if (window.innerWidth >= 992) {
                return 3;
            }
            if (window.innerWidth >= 576) {
                return 2;
            }
            return 1;
        }

        function getItemNaturalH(item, colWidth) {
            const img = item.querySelector('img');
            if (img && img.naturalWidth && img.naturalHeight) {
                return colWidth * (img.naturalHeight / img.naturalWidth);
            }
            return colWidth * (9 / 16);
        }

        function buildLayout() {
            const colCount  = getColCount();
            const colGap    = parseFloat(getComputedStyle(grid).columnGap) || 16;
            const gridWidth = grid.getBoundingClientRect().width;
            const colWidth  = (gridWidth - colGap * (colCount - 1)) / colCount;

            grid.innerHTML = '';

            const cols       = Array.from({ length: colCount }, () => {
                const col = document.createElement('div');
                col.className = 'wgm-col';
                grid.appendChild(col);
                return col;
            });
            const colHeights = new Array(colCount).fill(0);

            items.forEach(item => {
                const idx = colHeights.indexOf(Math.min(...colHeights));
                cols[idx].appendChild(item);
                colHeights[idx] += getItemNaturalH(item, colWidth);
            });

            if (!animated) {
                animated = true;
                const imgs = Array.from(grid.querySelectorAll('img'));
                gsap.fromTo(imgs,
                    { opacity: 0, scale: 0.88, clipPath: 'inset(10% 0% 10% 0%)' },
                    {
                        opacity: 1, scale: 1, clipPath: 'inset(0% 0% 0% 0%)',
                        duration: 1.2, ease: 'power3.out', stagger: 0.1,
                        scrollTrigger: {
                            trigger: grid,
                            start: 'top 72%',
                            toggleActions: 'play none none none'
                        }
                    }
                );
            }
        }

        const images  = Array.from(grid.querySelectorAll('img'));
        let   pending = images.filter(img => !img.complete).length;

        if (pending === 0) {
            buildLayout();
        } else {
            images.forEach(img => {
                if (!img.complete) {
                    img.addEventListener('load', () => {
                        if (--pending === 0) buildLayout(); }, { once: true });
                }
            });
        }

        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(buildLayout, 150);
        });
    });
}


export {initWorkIntroAnimation, initWorkBlockAnimations, initWorkDynamicContentAnimations, initMasonryGallery};
